function getCurrencyCode(locale) {
    const localeCurrencyMap = {
        'en-US': 'USD',
        'en-GB': 'GBP',
        'en-CA': 'CAD',
        'fr-FR': 'EUR',
        'de-DE': 'EUR',
    };
    return localeCurrencyMap[locale] || 'USD';
}

export const currency = new Intl.NumberFormat(navigator.language, {
    style: 'currency',
    currency: getCurrencyCode(navigator.language),
    currencyDisplay: 'narrowSymbol'
});

export const formatCurrency = (number, options = { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol' }) => {
    if (typeof number === 'string') number = parseFloat(number);
    if (isNaN(number)) return '-';

    const { style, currency, currencyDisplay } = options;
    const amount = typeof number === 'number' ? number : 0;
    const currencyFormatOptions = { style, currency, currencyDisplay };
    const currencyFormatter = new Intl.NumberFormat(navigator.language, currencyFormatOptions);
    return currencyFormatter.format(amount).replace(/[a-zA-Z]/g, '');
};

export const formatPhone = str => {
    if (!str) return '-';
    let match = ('' + str).replace(/\D/g, '').match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) return `(${match[1]}) ${match[2]}-${match[3]}`;
    return null
};

export const YYYYMMDD = date => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return year + '-' + month + '-' + day;
}

export const INVALID_DATE_STRING = 'Invalid Date';

export const shortHandDate = (date, invalid = '-') => {
    if (!date) return invalid;
    return new Date(date).toLocaleDateString(navigator.language, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
}

export const shortHandDateTime = (date, invalid = '-') => {
    if (!date) return invalid;
    const time = new Date(date).toLocaleTimeString(navigator.language, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    })
    return `${shortHandDate(date)} - ${time}`
};

export const shortHandTime = (date, invalid = '-') => {
    if (!date) return invalid;
    const time = new Date(date).toLocaleTimeString(navigator.language, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    })
    return time
};

export const shortHandDateTimeUTC = (date) => shortHandDateTime(new Date(date + 'Z'));

export const shortHandDateUTC = (date) => shortHandDate(new Date(date + 'Z'));

export const shortHandTimeUTC = (date) => shortHandTime(new Date(date + 'Z'));

export const offsetUTC = (date) => new Date(new Date(date).getTime() + (new Date(date).getTimezoneOffset() * 60000)).toISOString().split('T')[0];

export const generateId = (l = 7) => Array.from({ length: l }, () => String.fromCharCode(Math.random() > 0.5 ? randomIntBetween(65, 90) : randomIntBetween(97, 122))).join('');

export const randomIntBetween = (min, max) => Math.floor(Math.random() * (max - min + 1) + min);

export const duplicateSort = (o, k) => o.sort((a, b) => o.filter(e => e[k] === b[k]).length - o.filter(e => e[k] === a[k]).length);

export const between = (num, minN, maxN) => Math.min(Math.max(num, minN), maxN);

export const inputDate = (d = new Date()) => new Date(d).toISOString().split('T')[0];

export const beginningOfMonth = inputDate(new Date().setDate(1));

export const getDaysInMonth = (month = 1, year = 2000) => new Date(year, month, 0).getDate();

export const parseDateFromTimestamp = (dateStr) => {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split("T")[0].split("-").map(Number);
    return new Date(y, m - 1, d);
};


export const buildGetQuery = (url, query) => {
    return url += '?' + Object
        .entries(query)
        .filter(([key, value]) => value)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&');
}

export const planeDate = date => {
    const dates = date.split('-');
    return `${dates[1]}/${dates[2]}/${dates[0]}`
}

export const dec2Hex = (dec) => {
    const d2h = (dec) => {
        let hex = Number(dec).toString(16)
        if (hex.length < 2) { hex = '0' + hex }
        return hex
    }

    const R = Math.floor(dec / 65536);
    dec = dec % 65536
    const G = Math.floor(dec / 256);
    dec = dec % 256
    const B = dec;
    return `#${d2h(R)}${d2h(G)}${d2h(B)}`
}

export const hex2Dec = (color) => !color ? 0 : parseInt(color, 16);

export const timeAgo = date => {
    const now = Date.now();
    const seconds = (now - date) / 1000;
    const minutes = seconds / 60;
    const hours = minutes / 60;
    const days = hours / 24;
    const months = days / 30;
    const years = days / 365;

    if (seconds < 5) {
        return 'now';
    } else if (seconds < 60) {
        return `${Math.floor(seconds)}s`;
    } else if (minutes < 60) {
        return `${Math.floor(minutes)}m`;
    } else if (hours < 24) {
        return `${Math.floor(hours)}h`;
    } else if (days < 30) {
        return `${Math.floor(days)}d`;
    } else if (months < 12) {
        return `${Math.floor(months)}mo`;
    } else {
        return `${years.toFixed(1)}y`;
    }
}

export const isMobile = (agent) => {
    if (!agent) agent = navigator.userAgent;
    let check = false;
    // eslint-disable-next-line no-useless-escape
    (function (a) { if (/android|(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a) || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0, 4))) check = true; })(navigator.userAgent || navigator.vendor || window.opera);
    return check;
}

export const plural = (numberOf, word) => `${word}${numberOf > 1 ? 's' : ''}`
export const irregularPluralization = (numberOf, singular, many) => `${numberOf === 1 ? singular : many}`

export const updateNestedObjProp = (obj, path, value) => {
    if (!(path instanceof Array)) path = path.split('.');
    if (path.length === 1) {
        obj[path] = value;
        return
    }
    return updateNestedObjProp(obj[path[0]], path.slice(1), value)
}

export const schemafy = (obj) => {
    const schema = {
        '$schema': 'https://json-schema.org/draft/2019-09/schema',
        'additionalProperties': false
    };
    const drillDown = (obj, schema = {}) => {
        schema.type = obj instanceof Array ? 'array' : typeof obj;
        if (schema.type === 'object') {
            schema.additionalProperties = false;
            schema.required = Object.keys(obj);
            schema.properties = {};
            schema.required.forEach(e => schema.properties[e] = drillDown(obj[e]));
        } else if (schema.type === 'array') {
            schema.items = {};
            obj.forEach(e => schema.items = drillDown(e));
        } else {
            schema.default = obj;
        }
        return schema;
    };
    return drillDown(obj, schema);
}

export const pascalCase = string => string.replace(/(^|\s)\S/g, e => e.toUpperCase());

export const addSpacesToPascalCase = string => {
    if (typeof string !== 'string') return string;
    const spacedString = string.replace(/([A-Z])/g, ' $1').trim();
    const capitalizedString = spacedString.charAt(0).toUpperCase() + spacedString.slice(1);
    return capitalizedString;
}

export const gradient = (color1, color2, ratio) => {
    const from = hex2rgb(color1)
    const to = hex2rgb(color2)

    const r = Math.ceil(to.r * ratio + from.r * (1 - ratio));
    const g = Math.ceil(to.g * ratio + from.g * (1 - ratio));
    const b = Math.ceil(to.b * ratio + from.b * (1 - ratio));

    return `#${hex(r)}${hex(g)}${hex(b)}`;
}

const hex2rgb = color => {
    const hex = color.replace('#', '')
    return {
        r: parseInt(hex.substring(0, 2), 16),
        g: parseInt(hex.substring(2, 4), 16),
        b: parseInt(hex.substring(4, 6), 16),
    }
}

const hex = num => {
    num = num.toString(16);
    return (num.length === 1) ? '0' + num : num;
}

String.prototype.defaultMatch = function (match, defaultVal = '') {
    return (this.match(match) || [defaultVal])[0]
}

Array.prototype.random = function () {
    return this[Math.floor(Math.random() * this.length)]
}

export const mapObj = (obj, callback) => {
    const result = [];
    const temp = Object.entries(obj);
    for (let i = 0; i < temp.length; i++) {
        result.push(callback(temp[i], i, temp));
    }
    return result
}

export const lastKeyPress = {
    keyInternal: null,
    keyListener: val => { },
    set key(val) {
        this.keyInternal = val;
        this.keyListener(val);
    },
    get key() {
        return this.keyInternal;
    },
    registerListener: function (listener) {
        this.keyListener = listener;
    }
}

export const htmlDecode = input => {
    const e = document.createElement('div');
    e.innerHTML = input;
    return e.childNodes.length === 0 ? '' : e.childNodes[0].nodeValue;
};

export const shallowCompare = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2)

export const addSuffix = (num, type = 'suffix') => {
    const suff = type === 'plain' ? { 1: 'st', 2: 'nd', 3: 'rd', 4: 'th' } : { 1: 'ˢᵗ', 2: 'ⁿᵈ', 3: 'ʳᵈ', 4: 'ᵗʰ' }
    if (!num || parseInt(num) < 1) return '-';
    return `${num}`.match(/1[1-3]$/) ? `${num}${suff[4]}` : `${num}${suff[parseInt(num) % 10] || suff[4]}`
}

export const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const birthday = (month, day) => {
    if (month >= 1 && month <= 12) {
        return `${months[month - 1]} ${addSuffix(day)} `;
    } else {
        return false;
    }
}

export const getLocalDateTime = (date) => {
    const currentDate = date ?? new Date();

    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const hours = String(currentDate.getHours()).padStart(2, '0');
    const minutes = String(currentDate.getMinutes()).padStart(2, '0');
    const seconds = String(currentDate.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`
}

export const formatDateToHTML = (date = new Date(), monthStart) => {

    if (monthStart) date.setDate(1);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}

export const toShortDate = (value) => {
    if (!value) return null;

    // Already MM/DD/YYYY → return unchanged
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
        return value;
    }

    // YYYY-MM-DD → parse manually to avoid UTC interpretation
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split("-");
        return `${month}/${day}/${year}`;
    }

    // Date object or other date-like string
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d)) return null;

    // Use LOCAL getters to avoid timezone/UTC conversion
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();

    return `${month}/${day}/${year}`;
};



export const getMonths = () => {
    const months = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months.map((e, i) => ({
        label: e,
        value: i + 1
    }))
}

export const decodeHTMLforDB = text => {
    return text//.replace(/<br>/g, '<br data-text='true'>')
}

export const statusColors = {
    'Approved': '#44ce7b',
    'Active': '#44ce7b',
    'Paid': '#44ce7b',
    '200': '#44ce7b',
    '201': '#44ce7b',
    'Inactive': '#6b6b6b',
    'Canceled': '#6b6b6b',
    'CreditHold': '#ff4141',
    'Deleted': '#93090e',
    'NotValidated': '#bf0d14',
    'ChargeError': '#bf0d14',
    'Error': '#bf0d14',
    '500': '#bf0d14',
    '404': '#bf0d14',
    'EndBillingPending': '#ffa500',
    'Pending': '#ffa500',
    'Processing': '#ffa500',
};

export const exportToPDF = (ref, title) => {
    if (!ref) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<html><head><title>${title}</title></head><body>`);
    const styles = document.getElementsByTagName('style');
    for (let i = 0; i < styles.length; i++) {
        if (styles[i].getAttribute('data-styled')) {
            const cssText = Array.from(styles[i].sheet.cssRules).map(rule => (
                !rule.cssText.includes('@keyframes') && !rule.cssText.includes('animation:')
                    ? rule.cssText
                    : ''
            )).join('\n');
            const style = printWindow.document.createElement('style');
            style.appendChild(printWindow.document.createTextNode(cssText));
            printWindow.document.head.appendChild(style);
        } else {
            printWindow.document.write(styles[i].outerHTML);
        }
    }
    printWindow.document.write(`<div class='page'>${ref.current.outerHTML}</div>`);
    printWindow.document.write('</body></html>');
    printWindow.document.close();

    setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
    }, 100)
}

export const translate = (word) => {
    if (typeof word === 'boolean') {
        return word ? 'Yes' : 'No'
    }

    const wordMappings = {
        'AppWashClub': 'Wash Club',
        'AppClub': 'Wash Club',
        'PrepaidPass': 'ExpressPass',
        'BuyUp': 'Buy Ups',
        'Wash': 'Wash Purchases',
        'AppWashClubSignUp': 'App Wash Club Sign Ups',
        'AppWashClubBilling': 'App Wash Club Recurring Billing',
        'PrepaidPassBilling': 'ExpressPass Recurring Billing',
        'LPR': 'Kiosk Sign Ups',
        'CodeBundle': 'Kiosk Bundles',
        'AppWash': 'App Purchases',
        'AppAwardCode': 'App Award',
        'AppWashCode': 'App Code',
        'BundleCode': 'Bundle Code',
        'Time Sold': 'Bay Timer',
        'Gift': 'Gift Card',
        'ReceiptCode': 'Receipt Code',
        'CodeCoupon': 'Coupon Code',
        'AppBundle': 'App Bundle',
        'FleetPassBilling': 'Fleet Pass Billing',
        'ManualSale': 'Manual Sale',
        'PrepaidPassSignup': 'Prepaid Pass Signup',
        'TunnelCode': 'Tunnel Code',
        'ValueCardIssue': 'Value Card Issue',
        'ValueCardRecharge': 'Value Card Recharge',
        'VendPulses': 'Vend Pulses',
        'AppBundleCode': 'App Bundle Code',
        'AppDealCode': 'App Deal Code',
        'AppPromoDealCode': 'App Promo Deal Code',
        'AppWashClubTime': 'App Wash Club Time',
        'PrivateLabelCard': 'Private Label Card',
        'RewashCode': 'Rewash Code',
        'TokenCoin': 'Token Coin',
        'TokenCredit': 'Token Credit',
        'XpresWash': 'Xpres Wash',
        'HsWebSite': 'App',
        'GiftCardRecharge': 'Gift Card Recharge',
        'ItemRate': 'Item Rate',
        'DollarDiscTrans': 'Transaction Dollar Discount',
        'DollarDiscItem': 'Item Dollar Discount',
        'GiftCard': 'Gift Card',
        'LicensePlate': 'License Plate',
        'WebSite': 'Website',
        'WashClubReactivation': 'Wash Club Reactivation',
        'WashClubChange': 'Wash Club Change',
        'WashClubSignUp': 'Wash Club Sign Up',
        'AppId': 'App Scan',

        'True': 'Yes',
        'False': 'No',
        'KioskDefault': 'Kiosk Default',
        'UseDefault': 'Award Default',
    };
    return wordMappings[word] || word;
}

export const findLastNonFalsely = (arr) => {
    for (let i = arr.length - 1; i >= 0; i--) {
        if (arr[i]) {
            return i;
        }
    }
    return -1;
}

export const uniqueSet = (arr) => [...new Set(arr)];

export const toDHMS = (seconds) => {
    const days = Math.floor(seconds / (3600 * 24));
    seconds -= days * 3600 * 24;
    const hours = Math.floor(seconds / 3600);
    seconds -= hours * 3600;
    const minutes = Math.floor(seconds / 60);
    seconds -= minutes * 60;

    let result = [];
    if (days > 0) result.push(`${days}D`);
    if (hours > 0) result.push(`${hours}H`);
    if (minutes > 0) result.push(`${minutes}M`);
    if (seconds > 0) result.push(`${seconds}S`);

    return result.join(' ');
}

export const getLocal = (key) => {
    const data = window.localStorage.getItem(key);
    try {
        return JSON.parse(data)
    } catch (ex) {
        return ex
    }
}
export const setLocal = (key, item) => window.localStorage.setItem(key, JSON.stringify(item));
export const removeLocal = (key) => window.localStorage.removeItem(key);

export class Enum {
    constructor(listOfKeys, startingIndex = 0) {
        this.keys = [...listOfKeys];

        listOfKeys.forEach(e => {
            this[e] = startingIndex;
            this[startingIndex] = e;
            startingIndex++;
        });

        Object.freeze(this);
    }

    getKey(value) {
        const key = this[value];
        if (key === undefined) {
            throw new Error(`Invalid enum value: ${value}`);
        }
        return key;
    }
}



const getGPUInfo = () => {
    var canvas = document.createElement('canvas');
    var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    var gpuInfo = 'Unknown';
    if (gl) {
        var debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
            gpuInfo = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        }
    }
    return gpuInfo;
}

const getStorageInfo = (quota) => {
    return new Promise(resolve => {
        var storageInfo = {};
        if (navigator.storage && navigator.storage.estimate) {
            navigator.storage.estimate().then(function (estimate) {
                resolve(quota ? estimate.quota || 'Unknown' : estimate);
            });
        } else {
            storageInfo.usage = 'Unknown';
            storageInfo.quota = 'Unknown';
            resolve(quota ? storageInfo.quota : storageInfo);
        }
    });
}

const getNetworkInfo = async () => {
    let data = {};
    const rtcPeerConn = new RTCPeerConnection({ iceServers: [] });

    rtcPeerConn.createDataChannel('');

    try {
        const offerDesc = await rtcPeerConn.createOffer();
        await rtcPeerConn.setLocalDescription(offerDesc);
    } catch (error) {
        console.warn('Offer failed', error);
    }

    rtcPeerConn.onicecandidate = function (event) {
        if (event.candidate) {
            data = event.candidate;
        }
    };

    return new Promise(resolve => {
        rtcPeerConn.onicecandidate = function (event) {
            if (event.candidate) {
                resolve(event.candidate);
            } else {
                resolve(null);
            }
        };
    });
};

export const stringToHash = (seed) => {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        const char = seed.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0;
    }
    return 'ID-' + hash.toString(16);
}

export const getDeviceFingerprint = () => {
    const userAgent = navigator.userAgent;
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;
    const deviceMemory = navigator.deviceMemory || 'Unknown'; // Not supported in all browsers
    const cores = navigator.hardwareConcurrency || 'Unknown'; // Number of CPU cores
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const platform = navigator.platform;
    const language = navigator.language;

    return {
        userAgent: userAgent,
        screenResolution: `${screenWidth}x${screenHeight}`,
        deviceMemory: deviceMemory,
        cpuCores: cores,
        timeZone: timeZone,
        platform: platform,
        language: language
    };
}

export const collectHardwareInformation = () => {
    return new Promise(async (resolve) => {
        const hardwareInfo = {};
        hardwareInfo.cpu = navigator.hardwareConcurrency || 'Unknown';
        hardwareInfo.gpu = getGPUInfo();
        hardwareInfo.memory = navigator.deviceMemory || 'Unknown';
        hardwareInfo.storage = await getStorageInfo(true);
        hardwareInfo.timezoneOffset = new Date().getTimezoneOffset();
        hardwareInfo.screen = `${window.screen.width}x${window.screen.height}`;
        hardwareInfo.colorDepth = window.screen.colorDepth;
        hardwareInfo.userAgent = navigator.userAgent;
        hardwareInfo.browserLanguage = navigator.language;
        hardwareInfo.network = await getNetworkInfo();
        const { address, priority, protocol } = hardwareInfo.network;
        hardwareInfo.network = `${address.length > 15 ? 'MASKED' : address}:${priority}:${protocol}`
        resolve(hardwareInfo)
    })
}

export const getFingerprint = () => new Promise(async (resolve) => collectHardwareInformation().then(res => resolve(Object.values(res).join(','))));

export const gk = (l = 0x7, s = Math.random()) => {
    function m(s) {
        let t = s += 0x6D2B79F5;
        t = Math.imul(t ^ t >>> 0xF, t | 0x1);
        t ^= t + Math.imul(t ^ t >>> 0x7, t | 0x3D);
        return ((t ^ t >>> 0xE) >>> 0) / 0x100000000;
    }

    if (typeof s === 'string') {
        let h = 0;
        for (let i = 0; i < s.length; i++) {
            let c = s.charCodeAt(i);
            h = ((h << 0x5) - h) + c;
            h |= 0;
        }
        s = h;
    }

    let k = '';
    const q = (n, l = 0x6) => {
        const a = [n];
        let b = n;
        for (let i = 0; i < l; i++) {
            let t;
            (i < 0x2) ? t = 0x3 : (i === 0x2) ? t = 0x1 : (i < 0x5) ? t = 0x5 : t = 0x3;
            a.push(b + t);
            b += t;
        }
        return a;
    }
    const g = [q(0x41), q(0x61)].flat();
    const c = [
        Array.from({ length: 0x1A }, (_, i) => (0x41 + i)),
        Array.from({ length: 0x1A }, (_, i) => (0x61 + i)),
        Array.from({ length: 0xA }, (_, i) => (i))
    ].flat().filter(e => !g.includes(e))

    const lC = c.length;

    for (let i = 0; i < l; i++) {
        const r = Math.floor(m(s + i) * lC);
        k += r < lC - 0xA ? String.fromCharCode(c[r]) : c[r];
    }

    return k;
}

export const exportToCSV = (rows, filename = 'export.csv') => {
    const escape = (val) => {
        if (val === null || val === undefined) return '';
        const str = String(val);
        return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"`
            : str;
    };
    const csv = rows.map(row => row.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
};

export const dummyRequest = (e) => {
    return new Promise((resolve, reject) => {
        setTimeout(() => {
            if (e) alert(JSON.stringify(e, null, 2))
            resolve(true);
        }, 1500)
    });
}


const base64UrlDecode = (base64Url) => {
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');

    const binaryString = window.atob(padded);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    const jsonPayload = new TextDecoder('utf-8').decode(bytes);
    return JSON.parse(jsonPayload);
}

export const decodeJWT = (token) => {
    const parts = token.split('.');
    if (parts.length !== 3) {
        throw new Error('Invalid JWT');
    }

    const payload = parts[1];
    return base64UrlDecode(payload);
}


export const createNewUser = () => {
    const firstNames = [
        'John', 'Jane', 'Michael', 'Emily', 'David', 'Sarah',
        'James', 'Laura', 'Robert', 'Lisa', 'William', 'Mary',
        'Joseph', 'Linda', 'Charles', 'Karen', 'Thomas', 'Nancy',
        'Christopher', 'Betty', 'Daniel', 'Margaret', 'Matthew',
        'Sandra', 'Anthony', 'Ashley', 'Mark', 'Kimberly', 'Paul',
        'Jessica', 'Andrew', 'Donna', 'Joshua', 'Carol', 'Kenneth',
        'Michelle', 'Kevin', 'Dorothy', 'Brian', 'Amanda', 'George',
        'Melissa', 'Edward', 'Deborah', 'Ronald', 'Stephanie',
        'Timothy', 'Rebecca', 'Jason', 'Sharon'
    ];

    const lastNames = [
        'Smith', 'Johnson', 'Brown', 'Williams', 'Jones', 'Miller',
        'Davis', 'Garcia', 'Rodriguez', 'Martinez', 'Hernandez',
        'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas', 'Taylor',
        'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson',
        'White', 'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis',
        'Robinson', 'Walker', 'Young', 'Allen', 'King', 'Wright',
        'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores', 'Green',
        'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell',
        'Mitchell', 'Carter', 'Roberts'
    ];

    const carMakes = ['Toyota', 'Ford', 'Honda', 'Chevrolet', 'BMW', 'Mercedes', 'Audi', 'Nissan', 'Hyundai', 'Volkswagen'];

    const carModels = {
        Toyota: ['Camry', 'Corolla', 'Prius', 'RAV4', 'Highlander'],
        Ford: ['Mustang', 'F-150', 'Explorer', 'Focus', 'Escape'],
        Honda: ['Civic', 'Accord', 'CR-V', 'Pilot', 'Fit'],
        Chevrolet: ['Impala', 'Malibu', 'Tahoe', 'Silverado', 'Equinox'],
        BMW: ['3 Series', '5 Series', 'X5', 'X3', '7 Series'],
        Mercedes: ['AMG', 'C-Class', 'E-Class', 'S-Class', 'GLC'],
        Audi: ['A3', 'A4', 'A6', 'Q5', 'Q7'],
        Nissan: ['Altima', 'Sentra', 'Maxima', 'Rogue', 'Murano'],
        Hyundai: ['Elantra', 'Sonata', 'Tucson', 'Santa Fe', 'Kona'],
        Volkswagen: ['Golf', 'Passat', 'Tiguan', 'Jetta', 'Atlas']
    };

    const streetNames = [
        'Main St', 'Elm St', 'Oak St', 'Pine St', 'Maple St',
        'Cedar St', 'Walnut St', 'Chestnut St', 'Willow St',
        'Birch St', 'Broadway', 'Highland Ave', 'Park Ave',
        'River Rd', 'Spring St', '1st St', '2nd St', '3rd St',
        '4th St', '5th St', 'Washington St', 'Lincoln St',
        'Madison St', 'Jefferson St', 'Franklin St', 'Jackson St',
        'Adams St', 'Harrison St', 'Church St', 'School St',
        'Lake St', 'Hill St', 'Sunset Blvd', 'Bay St', 'Valley Rd',
        'Mountain Rd', 'Garden St', 'King St', 'Queen St',
        'Prince St', 'Princess St', 'Victoria St', 'Grand Ave',
        'Market St', 'Center St', 'Union St', 'Liberty St',
        'College St', 'State St', 'Front St', 'River St', 'Railroad Ave'
    ];

    const cityNames = [
        'Springfield', 'Riverside', 'Franklin', 'Greenville', 'Fairview',
        'Madison', 'Georgetown', 'Arlington', 'Salem', 'Bristol',
        'Clinton', 'Ashland', 'Oxford', 'Centerville', 'Kingston',
        'Manchester', 'Lexington', 'Cambridge', 'Fairfield', 'Milton',
        'Lancaster', 'Hillside', 'Midland', 'Newport', 'Washington',
        'Hamilton', 'Clayton', 'Cleveland', 'Dayton', 'Hudson',
        'Jacksonville', 'Burlington', 'Winchester', 'Easton',
        'Westfield', 'Lawrence', 'Dover', 'Bridgeport', 'Newport',
        'Greenwood', 'Mount Pleasant', 'Portland', 'Summit',
        'Lakewood', 'Oakland', 'Rockford', 'Norfolk', 'Richmond',
        'Brockton', 'Waterford', 'Troy'
    ];

    const carColors = [
        'Red', 'Blue', 'Black', 'White', 'Silver', 'Green',
        'Yellow', 'Orange', 'Purple', 'Pink', 'Brown',
        'Grey', 'Gold', 'Teal', 'Burgundy', 'Navy',
        'Lime', 'Cyan', 'Magenta', 'Maroon'
    ];

    const states = [
        'US-AK', 'US-AL', 'US-AR', 'US-AS', 'US-AZ', 'US-CA',
        'US-CO', 'US-CT', 'US-DC', 'US-DE', 'US-FL', 'US-GA',
        'US-GU', 'US-HI', 'US-IA', 'US-ID', 'US-IL', 'US-IN',
        'US-KS', 'US-KY', 'US-LA', 'US-MA', 'US-MD', 'US-ME',
        'US-MI', 'US-MN', 'US-MO', 'US-MP', 'US-MS', 'US-MT',
        'US-NC', 'US-ND', 'US-NE', 'US-NH', 'US-NJ', 'US-NM',
        'US-NV', 'US-NY', 'US-OH', 'US-OK', 'US-OR', 'US-PA',
        'US-PR', 'US-RI', 'US-SC', 'US-SD', 'US-TN', 'US-TX',
        'US-UM', 'US-UT', 'US-VA', 'US-VI', 'US-VT', 'US-WA',
        'US-WI', 'US-WV', 'US-WY'
    ]

    const newUser = {
        email: '',
        confirmEmail: '',
        firstName: firstNames.random(),
        lastName: lastNames.random(),
        cellPhoneNumber: `${randomIntBetween(100, 999)}${randomIntBetween(100, 999)}${randomIntBetween(1000, 9999)}`,
        zipPostalCode: randomIntBetween(10000, 99999),
        mediaId: randomIntBetween(1000000, 9999999),
        birthdayMonth: randomIntBetween(1, 12),
        birthdayDay: randomIntBetween(1, 28),
        license: generateId(6).replace(/./g, (char) => char.toUpperCase()),
        year: randomIntBetween(1995, new Date().getFullYear() + 2),
        make: carMakes.random(),
        color: carColors.random(),

        address1: `${randomIntBetween(1, 99999)} ${streetNames.random()}`,
        city: cityNames.random(),
        state: states.random(),
    }
    const models = carModels[newUser.make];
    newUser.model = models.random();
    newUser.email = newUser.confirmEmail = `${newUser.firstName.charAt(0)}${newUser.lastName}+${randomIntBetween(10, 100)}@Hamilton.com`

    return newUser
}

export const randomNum = (seed = Math.random()) => {
    if (typeof seed === 'string') {
        seed = Array.from(seed).reduce((a, b) => a + b.charCodeAt(0), 0);
    }
    const a = 1664525;
    const c = 1013904223;
    const m = Math.pow(2, 32);
    const lcg = (seed) => ((a * seed + c) % m) / m;
    return lcg(seed);
}


export const isNoWrapString = (str) => {
    //regex patterns for strings that should avoid line breaks/wrapping
    //e.g. codes, guids, datetime, phone numbers, etc.
    //useful to conditionally set no-wrap style behavior
    const noWrapPatterns = [
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/,                                   // Email
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/, // ISO timestamp
        /^\d{4}-\d{2}-\d{2}$/,                                          // Date (YYYY-MM-DD)
        /^\d{2}:\d{2}(:\d{2})?$/,                                       // Time (HH:mm[:ss])
        /^\d{1,2}\/\d{1,2}\/\d{4} - \d{1,2}:\d{2} (AM|PM)$/,             // Short timestamp
        /^\d{1,3}(\.\d{1,3}){3}$/,                                      // IP address-ish
        /^[A-Za-z0-9_-]+$/,                                             // Alphanumeric codes / GUIDs
        /^\$?\d{1,3}(,\d{3})*(\.\d{2})?$/,                              // Currency
        /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/,                        // Phone
        /^[0-9A-Fa-f]{2}(:[0-9A-Fa-f]{2}){5}$/                          // MAC Addresses
    ];
    
    return noWrapPatterns.some(regex => regex.test(str));
}

export const getPortalTarget = ({ contextEl } = {}) => {
    const fullscreenEl = document.fullscreenElement;

    // If we have a context element, use it—unless fullscreen is active and it’s outside fullscreen DOM
    if (contextEl) {
        if (!fullscreenEl) return contextEl;
        if (fullscreenEl.contains(contextEl)) return contextEl;
        // fullscreen active but context is outside → must portal into fullscreen
    }

    // Fullscreen fallback
    if (fullscreenEl) {
        const fsRoot = fullscreenEl.querySelector('[id^="fullscreen-overlay-root-"]');
        if (fsRoot) return fsRoot;
        return fullscreenEl; // last-resort inside fullscreen
    }

    // Non-fullscreen fallback
    return document.body;
};


export const getBillingCycle = (cycle) => {
    switch (cycle) {
        case 1:
            return 'Monthly';
            break;
        case 12:
            return 'Yearly';
            break;
        default:
            return '-';
    }
}



